const GENDER_PREFERENCE = {
  male: ['open', 'mixed', 'hybrid', 'ladies'],
  female: ['ladies', 'mixed', 'hybrid', 'open'],
};

const competitionCodeForTeam = (team, divisions, competitions) => {
  const division = (divisions ?? []).find((row) => row.id === team.division_id);
  const competition = (competitions ?? []).find((row) => row.id === division?.competition_id);
  return competition?.code ?? '';
};

const findKeptTeam = (teams, previousTeam) => {
  if (!previousTeam) return null;
  return (
    teams.find((team) => previousTeam.id && team.id === previousTeam.id)
    || teams.find((team) => previousTeam.source_team_id && team.source_team_id === previousTeam.source_team_id)
    || teams.find((team) => previousTeam.name && team.name === previousTeam.name)
    || null
  );
};

export const isLeagueFixtureComplete = (fixture) => {
  if (!fixture) return false;
  if (fixture.home_score != null && fixture.away_score != null) return true;
  if (fixture.walkover_label) return true;
  return fixture.fixture_status === 1 || fixture.fixture_status_label === 'cancelled';
};

const fixtureDateKey = (fixture) => `${fixture?.match_date || ''}\t${fixture?.match_time || ''}`;

/**
 * Next unplayed match first (soonest date), TBA without a date after those,
 * then completed results newest first.
 */
export const sortLeagueFixturesUpcomingFirst = (fixtures = []) =>
  [...fixtures].sort((a, b) => {
    const aDone = isLeagueFixtureComplete(a);
    const bDone = isLeagueFixtureComplete(b);
    if (aDone !== bDone) return aDone ? 1 : -1;
    const aKey = fixtureDateKey(a);
    const bKey = fixtureDateKey(b);
    if (!a.match_date && !b.match_date) return 0;
    if (!a.match_date) return 1;
    if (!b.match_date) return -1;
    return aDone ? bKey.localeCompare(aKey) : aKey.localeCompare(bKey);
  });

/**
 * Default club League team: the viewer's explicit choice, otherwise a team
 * they have appeared for, preferring their gender's competitions.
 * Falls back to the first club team when they have no appearances.
 *
 * @returns {Object|null}
 */
export const pickDefaultClubLeagueTeam = ({
  teams,
  divisions,
  competitions,
  viewerTeamIds,
  viewerGender,
  previousTeam,
} = {}) => {
  const list = teams ?? [];
  if (!list.length) return null;

  const kept = findKeptTeam(list, previousTeam);
  if (kept) return kept;

  const played = new Set(viewerTeamIds ?? []);
  const mine = list.filter((team) => played.has(team.id));
  if (!mine.length) return list[0];

  const order = GENDER_PREFERENCE[viewerGender] ?? ['mixed', 'open', 'ladies', 'hybrid'];
  const preferenceKey = (code) => {
    if (code === 'mens' || code === 'men' || code === 'male' || code.startsWith('men')) return 'open';
    return code;
  };
  return [...mine].sort((a, b) => {
    const rank = (team) => {
      const index = order.indexOf(preferenceKey(competitionCodeForTeam(team, divisions, competitions)));
      return index === -1 ? order.length : index;
    };
    const diff = rank(a) - rank(b);
    if (diff !== 0) return diff;
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  })[0];
};

/**
 * Appearance cards for the My Club league carousel: only fixtures where the
 * player lined up for a team belonging to this Shuttlr club's linked county club.
 */
export const buildLinkedClubAppearanceMatches = ({
  fixtures = [],
  lineups = [],
  teams = [],
  leagueClubId,
} = {}) => {
  if (!leagueClubId) return [];

  const teamsById = new Map(teams.map((team) => [team.id, team]));
  const lineupByFixture = new Map(lineups.map((row) => [row.fixture_id, row]));

  return fixtures.flatMap((fixture) => {
    const lineup = lineupByFixture.get(fixture.id);
    const teamId = lineup?.team_id
      || (lineup?.side === 'home' ? fixture.home_team_id : fixture.away_team_id);
    const team = teamsById.get(teamId);
    if (!team || team.league_club_id !== leagueClubId) return [];

    const isHome = teamId === fixture.home_team_id;
    const opponentId = isHome ? fixture.away_team_id : fixture.home_team_id;
    const us = isHome ? fixture.home_score : fixture.away_score;
    const them = isHome ? fixture.away_score : fixture.home_score;
    return [{
      fixture,
      isHome,
      teamId,
      teamName: team.name ?? 'Team',
      opponentName: teamsById.get(opponentId)?.name ?? 'Opponent',
      us,
      them,
      result: us == null || them == null ? null : us > them ? 'won' : us < them ? 'lost' : 'drawn',
    }];
  });
};
